import { motion } from "framer-motion";
import { riseFade } from "@/lib/motion";

type Entry = { time: string; text: string; tone?: "soft" | "cost" | "win" };

const without: Entry[] = [
  { time: "9:00", text: "School run" },
  { time: "11:00", text: "The big meeting" },
  { time: "2:00", text: "A dip you didn't see coming", tone: "cost" },
  { time: "4:00", text: "Cancel dinner with friends", tone: "cost" },
  { time: "–", text: "Guilt. Again.", tone: "cost" },
];

const withMyelina: Entry[] = [
  { time: "6:30", text: "60-second check-in" },
  { time: "7:00", text: "Myelina: peak window 9–12", tone: "win" },
  { time: "9:00", text: "School run" },
  { time: "10:30", text: "Meeting moved earlier", tone: "win" },
  { time: "2:00", text: "Rest, planned" },
  { time: "6:00", text: "Dinner kept", tone: "win" },
];

const Column = ({
  title,
  entries,
  emphasis,
}: {
  title: string;
  entries: Entry[];
  emphasis: "muted" | "primary";
}) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-10% 0px" }}
    variants={riseFade}
    className={
      "relative rounded-2xl border p-7 sm:p-9 " +
      (emphasis === "primary"
        ? "border-primary/20 bg-card shadow-[0_30px_80px_-40px_hsl(var(--primary)/0.35)]"
        : "border-border/60 bg-card/60")
    }
  >
    <div className="text-[10px] tracking-[0.32em] uppercase text-muted-foreground mb-5">
      {title}
    </div>
    <ol className="space-y-3.5 font-body">
      {entries.map((e, i) => (
        <li key={i} className="flex gap-4 items-baseline">
          <span className="text-xs tabular-nums text-muted-foreground w-12 shrink-0">
            {e.time}
          </span>
          <span
            className={
              "text-[15px] leading-snug " +
              (e.tone === "cost"
                ? "text-foreground/80 line-through decoration-foreground/30"
                : e.tone === "win"
                ? "text-foreground"
                : "text-foreground/85")
            }
          >
            {e.tone === "cost" && (
              <span className="sr-only">Cancelled: </span>
            )}
            {e.tone === "win" && (
              <span className="sr-only">Planned: </span>
            )}
            {e.text}
          </span>
        </li>
      ))}
    </ol>
  </motion.div>
);

const DayWithWithout = () => (
  <section
    aria-labelledby="day-heading"
    className="relative py-24 sm:py-32 bg-background"
  >
    <div className="container mx-auto px-6 sm:px-10 lg:px-16">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-10% 0px" }}
        variants={riseFade}
        className="max-w-3xl mx-auto text-center mb-14 sm:mb-20 space-y-5"
      >
        <div className="eyebrow justify-center mx-auto">One quiet check-in</div>
        <h2 id="day-heading" className="font-display text-foreground text-display-xl">
          The difference is the <span className="italic text-primary">whole day.</span>
        </h2>
        <p className="lead-italic max-w-2xl mx-auto">
          Same Tuesday. Two versions. In one, fatigue holds the pen, in the other, you do.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8 max-w-5xl mx-auto">
        <Column title="A day without Myelina" entries={without} emphasis="muted" />
        <Column title="A day with Myelina" entries={withMyelina} emphasis="primary" />
      </div>
    </div>
  </section>
);

export default DayWithWithout;
